import { LogGroup, LogGroupOptions } from '@mbrtargeting/metatag-shared-types';
import { cssText, getUppermostReachableWindow, ReplaySubject, simpleStorageSwitch, simpleUrlSwitch } from '@mbrtargeting/metatag-utils';

/**
 * The different log levels.
 */
export enum LogLevel {
    DEBUG = 10,
    INFO = 20,
    NOTICE = 30,
    DEPRECATED = 35,
    WARNING = 40,
    ERROR = 50,
    CRITICAL = 60,
    ALERT = 70,
    EMERGENCY = 80
}

/**
 * The structure of a log entry.
 */
export interface LogEntry extends LogGroupOptions {
    date: Date;
    logLevel: LogLevel;
    message: string;
    messageObjects?: any[];
    stack?: string;
}

/**
 * A log sink that outputs log entries to the browser console with colors.
 */
class ColorfulConsole {

    /**
     * Creates a colorful console log sink that subscribes to the given subject.
     *
     * @param subject the log entry subject to subscribe to
     */
    constructor(subject: ReplaySubject<LogEntry> & { logSink?: ColorfulConsole }) {
        if (!subject.logSink) {
            // prevent double subscription
            subject.logSink = this;
            // reading the wanted console log level from url or storage entry
            const consoleLogLevel = +(simpleUrlSwitch(/sdgConsole=([0-9]+)/i) || '') || +(simpleStorageSwitch('sdgDumpLogsToConsole', /^([0-9]+)$/) || '') || LogLevel.ERROR;
            // send the logs to the browser console
            subject
                .filter(entry => entry.logLevel >= consoleLogLevel)
                .subscribe(entry => this.#sendLogToConsole(entry));
        }
    }

    /**
     * The prepared CSS styles for the different log parts.
     */
    #styles = (() => {
        // some shared styling
        const baseCss: Partial<CSSStyleDeclaration> = { display: 'inline-block', marginRight: '2px', padding: '2px 4px', borderRadius: '5px' };
        // prepare all styles to make logging faster
        return {
            identStyle: cssText({ ...baseCss, color: '#fff', backgroundColor: '#005276' }),
            levelStyles: {
                [LogLevel.DEBUG]: cssText({ ...baseCss, color: '#000', backgroundColor: '#bfdf6a' }),
                [LogLevel.INFO]: cssText({ ...baseCss, color: '#000', backgroundColor: '#00cccd' }),
                [LogLevel.NOTICE]: cssText({ ...baseCss, color: '#fff', backgroundColor: '#0951fb' }),
                [LogLevel.DEPRECATED]: cssText({ ...baseCss, color: '#000', backgroundColor: '#F84DF8' }),
                [LogLevel.WARNING]: cssText({ ...baseCss, color: '#000', backgroundColor: '#ffac14' }),
                [LogLevel.ERROR]: cssText({ ...baseCss, color: '#000', backgroundColor: '#ff7878' }),
                [LogLevel.CRITICAL]: cssText({ ...baseCss, color: '#000', backgroundColor: '#ff5050' }),
                [LogLevel.ALERT]: cssText({ ...baseCss, color: '#fff', backgroundColor: '#ff2828' }),
                [LogLevel.EMERGENCY]: cssText({ ...baseCss, color: '#fff', backgroundColor: '#f00050' }),
            },
            metaStyle: cssText({ ...baseCss, color: '#666', backgroundColor: '#fff' }),
            messageStyle: '',
        };
    })();

    /**
     * Extract the caller source line from a standard stack trace.
     *
     * TODO: The format of the stack trace is browser dependent and may not work in non-chrome-based browsers.
     *
     * @param stack a stack trace string
     * @param logLevel the log level to determine the correct line number; for DEPRECATED logs, we want to log the line of the caller of the deprecated function
     * @returns a source line in the format "file:line:column"
     */
    #getSourceLineFromStack(stack: string, logLevel: LogLevel): string {
        const index: number = logLevel === LogLevel.DEPRECATED ? 3 : 2;
        const lines: string[] = stack?.split('\n') ?? [];
        if (lines.length < index + 1) return '';
        // extract data via regex - format is usually: "at functionName (filePath:lineNumber:columnNumber)" or "at filePath:lineNumber:columnNumber"
        const [match, filePath, lineNumber, columnNumber] = lines[index].trim().match(/at\s+(?:.+\s+\()?(.+):(\d+):(\d+)\)?/) ?? [];
        return match ? `${filePath}:${lineNumber}:${columnNumber}` : '';
    }

    /**
     * Send a log entry to the browser console with colors.
     *
     * @param param0 a log entry to log
     */
    #sendLogToConsole({ logLevel, message, messageObjects = [], system = '', stack = '' }: LogEntry): void {
        const { identStyle, levelStyles, metaStyle, messageStyle } = this.#styles;
        const sourceLine = (logLevel >= LogLevel.DEPRECATED) ? this.#getSourceLineFromStack(stack, logLevel) : '';
        window.console?.log?.apply(this, [`%cSDG%c${LogLevel[logLevel].padStart(10)}%c${system.padEnd(10)}%c ${message}`, identStyle, levelStyles[logLevel], metaStyle, messageStyle, ...messageObjects, sourceLine]);
    }
}

/**
 * The symbol to store the logger subject in the uppermost reachable window.
 */
export const LOGGER_SYMBOL = Symbol.for('http://stroeerdev.de/logger');

// the uppermost reachable window object with eventually existing logger subject
const uppermostReachableWindow = getUppermostReachableWindow<Window & { [LOGGER_SYMBOL]?: ReplaySubject<LogEntry> }>();

/**
 * The subject where all log entries are stored internally.
 *
 * Lookup existing logEntries$ via Symbol from the uppermost reachable window or creates a new ReplaySubject.
 */
export const logEntries$: ReplaySubject<LogEntry> = uppermostReachableWindow[LOGGER_SYMBOL] ||= new ReplaySubject<LogEntry>(1e6);

/**
 * Creates a log group with a given prefix.
 *
 * @param param0 The options for the log group.
 * @returns The log helper functions.
 */
export const logGroup = <OPTIONS extends LogGroupOptions>(options: OPTIONS): LogGroup<OPTIONS> => {
    const { prefix } = options;
    // logger factory function
    const logFn = (logLevel: LogLevel) => (message: string, messageObjects?: any[]) => logEntries$.next({
        ...options,
        date: new Date(),
        logLevel,
        message: prefix ? `${prefix}: ${message}` : message,
        messageObjects,
        stack: new Error().stack,
    });
    // returning log helper functions
    return {
        logGroup: (options2: Partial<OPTIONS>) => logGroup({ ...options, ...options2 }),
        debug: logFn(LogLevel.DEBUG),
        info: logFn(LogLevel.INFO),
        notice: logFn(LogLevel.NOTICE),
        deprecated: logFn(LogLevel.DEPRECATED),
        warn: logFn(LogLevel.WARNING),
        error: logFn(LogLevel.ERROR),
        critical: logFn(LogLevel.CRITICAL),
        alert: logFn(LogLevel.ALERT),
        fatal: logFn(LogLevel.EMERGENCY),
    }
}

/**
 * Setup the colorful console log sink.
 */
new ColorfulConsole(logEntries$);
